import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Heart, Palette, Eye } from 'lucide-react';

const MeetMePage = () => {
  const values = [
    {
      icon: <Eye className="h-6 w-6" />,
      title: 'Intentional Design',
      description: 'Every choice matters. I focus on pieces and spaces that serve a purpose while bringing beauty to your everyday life.'
    },
    {
      icon: <Palette className="h-6 w-6" />,
      title: 'Coastal Aesthetic',
      description: 'Inspired by San Diego\'s natural beauty—light-filled spaces, soft neutrals, and the calm energy of the California coast.'
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: 'Personal Connection',
      description: 'Your style is uniquely yours. I take time to understand your lifestyle, preferences, and goals before creating your vision.'
    }
  ];

  return (
    <div data-testid="meet-me-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#E5E7EB]">
                <img
                  src="https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/hxdu8ajd_Denise%20July%202025.jpeg"
                  alt="Denise - Personal Stylist & Interior Designer"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#D4A373]/20 rounded-full -z-10" />
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#84A98C]/20 rounded-full -z-10" />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                Meet Me
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-8 leading-tight">
                Hello, I'm Denise
              </h1>
              <div className="space-y-6 text-[#57534E] font-sans text-lg leading-relaxed">
                <p>
                  Welcome to Maison LouLou. I'm a personal stylist and interior designer 
                  based in beautiful San Diego, California. My passion lies in helping 
                  people create wardrobes and living spaces that feel both effortless 
                  and intentional.
                </p>
                <p>
                  Living on the California coast, I developed a deep appreciation 
                  for the relaxed elegance that defines our lifestyle here—clean lines, 
                  natural textures, and spaces filled with light. This aesthetic guides 
                  everything I do.
                </p>
                <p>
                  My whole life, I've loved making my surroundings beautiful and 
                  helping my friends and family create beautiful spaces for themselves 
                  while staying true to their own personal styles. That passion has 
                  naturally evolved into what I do today.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Approach */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              My Approach
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50] max-w-2xl mx-auto">
              Design Philosophy
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="text-center p-8"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F9F7F2] text-[#D4A373] mb-6">
                  {value.icon}
                </div>
                <h3 className="font-serif text-2xl text-[#2C3E50] mb-4">
                  {value.title}
                </h3>
                <p className="font-sans text-[#57534E] leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <blockquote className="font-serif text-3xl md:text-4xl text-[#2C3E50] leading-relaxed italic">
            "I believe that when your surroundings align with who you are, 
            everything else falls into place. Your home should be your haven. 
            Your wardrobe should be your armor."
          </blockquote>
          <div className="mt-8">
            <p className="font-sans text-[#D4A373] text-lg">— Denise</p>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                My Journey
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50] mb-8">
                My Journey
              </h2>
              <div className="space-y-6 text-[#57534E] font-sans text-lg leading-relaxed">
                <p>
                  Over the years, I've had the joy of remodeling several of my own homes, 
                  transforming each space into a reflection of my evolving style and the 
                  coastal aesthetic I love so much.
                </p>
                <p>
                  Along the way, I've helped countless friends and family members create 
                  beautiful surroundings for themselves—spaces and wardrobes that feel 
                  authentically theirs while bringing more beauty into their everyday lives.
                </p>
                <p>
                  Recently, I made the decision to leave the corporate world and focus 
                  fully on what I'm most passionate about: design and style. It's been 
                  the most rewarding journey, and I'm thrilled to now share this passion 
                  with you.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/i1bym2ia_image.png"
                  alt="Coastal bedroom design"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/to9uodrf_image.png"
                  alt="Beautiful walk-in closet"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#2C3E50] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Let's Work Together
          </h2>
          <p className="font-sans text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            I'd love to learn about your style goals and help you create 
            spaces and wardrobes that truly reflect who you are.
          </p>
          <Link to="/contact">
            <Button
              data-testid="meet-me-cta-btn"
              className="rounded-full bg-[#D4A373] hover:bg-[#c29260] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              Book a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MeetMePage;
